import axiosClient from './axiosClient';

export const ipoService = {
  /**
   * Fetch paginated list of IPOs
   * @param {Object} params - Query params like { page: 1, page_size: 50 }
   */
  getIpos: async (params = { page: 1, page_size: 50 }) => {
    const response = await axiosClient.get('/api/core/ipos', { params });
    return response.data;
  },

  /**
   * Fetch single IPO details by ID
   * @param {string|number} id - The IPO ID
   */
  getIpoById: async (id) => {
    const response = await axiosClient.get(`/api/core/ipos/${id}`);
    return response.data;
  },

  /**
   * Fetch the FAQs generated for one IPO
   * @param {string|number} id - The IPO ID
   */
  getIpoFaqs: async (id) => {
    const response = await axiosClient.get(`/api/core/ipos/${id}/faqs`);
    return response.data;
  },

  /**
   * Fetch the broker research calls published for one IPO
   * @param {string|number} id - The IPO ID
   */
  getBrokerReviews: async (id) => {
    const response = await axiosClient.get(`/api/core/ipos/${id}/broker-reviews`);
    return response.data;
  },

  /**
   * Fetch one page of the SME IPO list (NSE Emerge & BSE SME)
   * @param {Object} params - e.g. { page: 2 }; the API pages by page / page_size
   */
  getSmeIpos: async (params = {}) => {
    const response = await axiosClient.get('/api/core/ipos/sme', { params });
    return response.data;
  },

  /**
   * Fetch broker research calls for every SME IPO, in one request
   */
  getSmeReviews: async () => {
    const response = await axiosClient.get('/api/core/ipos/sme/reviews');
    return response.data;
  },

  /**
   * Fetch live market IPOs (Current and Upcoming)
   * @param {boolean} force - Force refresh flag
   */
  getLiveIpos: async (force = false) => {
    const response = await axiosClient.get('/api/market/ipos/live', {
      params: { force },
    });
    return response.data;
  },

  /**
   * Fetch IPOs with confirmed bidding dates that haven't opened yet
   */
  getUpcomingIpos: async () => {
    const response = await axiosClient.get('/api/market/ipos/upcoming');
    return response.data;
  },

  /**
   * Fetch SME IPOs (NSE Emerge) with confirmed bidding dates that haven't opened yet
   */
  getSmeUpcomingIpos: async () => {
    const response = await axiosClient.get('/api/market/ipos/sme/upcoming');
    return response.data;
  },

  /**
   * Fetch category-wise subscription (QIB / NII / Retail) for every IPO NSE is tracking
   */
  getSubscriptionSummary: async () => {
    const response = await axiosClient.get('/api/market/subscription');
    return response.data;
  },

  /**
   * Fetch category-wise subscription for SME IPOs (NSE Emerge) that are open for bidding
   */
  getSmeSubscriptionSummary: async () => {
    const response = await axiosClient.get('/api/market/subscription/sme');
    return response.data;
  },

  /**
   * Most subscribed IPOs, ranked by times subscribed
   * GET /api/market/subscription/top
   * @param {object} params - { limit, board } ('sme' or 'mainboard'); the API defaults to 10 rows
   */
  getTopSubscription: async (params = {}) => {
    const response = await axiosClient.get('/api/market/subscription/top', { params });
    return response.data;
  },

  /**
   * DRHP / RHP filings tracker: board, regulatory status and filing dates
   * GET /api/core/ipos/drhp-tracker
   * @param {object} params - optional filters, e.g. { board: 'sme' }
   */
  getDrhpTracker: async (params = {}) => {
    const response = await axiosClient.get('/api/core/ipos/drhp-tracker', { params });
    return response.data;
  },

  /**
   * One tracker filing with everything extracted from its DRHP/RHP
   * GET /api/core/ipos/drhp-tracker/{id}
   * @param {string|number} id - The tracker record id (same id as the list)
   */
  getDrhpTrackerFiling: async (id) => {
    const response = await axiosClient.get(`/api/core/ipos/drhp-tracker/${id}`);
    return response.data;
  },

  /**
   * DRHP filings under SEBI review and RHP-stage approvals, newest filing first
   * GET /api/core/ipos/sebi-filings-approvals
   *
   * The endpoint takes limit (1-200, default 50), status, board and q, but has
   * no page or offset, so one request with limit=200 is the whole list.
   * @param {object} params - e.g. { limit: 200 }
   */
  getSebiFilingsApprovals: async (params = {}) => {
    const response = await axiosClient.get('/api/core/ipos/sebi-filings-approvals', {
      params,
      // A cold start of this endpoint was measured at 43 s, past the client's
      // 30 s default; a warm call answers in under a second
      timeout: 60000,
    });
    return response.data;
  },

  /**
   * Category-wise bid details for one issue (NSE bid book)
   * GET /api/market/subscription/{symbol}?force=false
   * @param {string} symbol - NSE symbol, e.g. "VARMORA"
   * @param {boolean} force - bypass the backend cache
   */
  getSubscriptionBySymbol: async (symbol, force = false) => {
    const response = await axiosClient.get(`/api/market/subscription/${encodeURIComponent(symbol)}`, {
      params: { force },
    });
    return response.data;
  },
};

export default ipoService;
